import React from 'react';
import PortableText from 'libs/sanity/components/PortableText';
import Text from 'components/Text';
import SanityBackgroundMediaSwitch from '@fogia/core-web/components/SanityMedia/SanityBackgroundMediaSwitch';
import { getLayoutImageSizes } from '@fogia/core-web/utils/getLayoutImageSizes';
import type {
    ButtonLinkArrayComponent,
    ImageTextContent,
    MediaItemResponsiveComponent,
} from 'libs/sanity/types/generated';
import styled from 'libs/styled';
import Button from 'components/Button';
import { above } from '@fogia/core-web/utils/mediaqueries';

export interface ImageTextBlockComponentProps {
    content?: ImageTextContent;
    media?: MediaItemResponsiveComponent;
    layout?: 'fullWidth' | 'halfWidth' | 'quarterWidth';
    imageLoading?: 'eager' | 'lazy';
}

const Container = styled('div')`
    width: 100%;
    background-color: var(--ui-background, #fffdf5);
`;

const ImageContainer = styled('div')`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 5;

    &[data-layout='fullWidth'] {
        aspect-ratio: 4 / 5;

        ${above['tablet.lg']} {
            aspect-ratio: 3 / 2;
        }
    }
`;

const TextArea = styled('div')`
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &[data-layout='fullWidth'] {
        padding: 10px var(--grid-padding, 10px);

        ${above['tablet.lg']} {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
        }
    }
`;

const TextContent = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 10px;

    p {
        text-indent: 30px;
    }

    &[data-layout] {
        max-width: 100%;

        ${above['tablet.lg']} {
            width: 50%;
        }
    }

    &[data-layout='quarterWidth'] {
        width: 100%;

        ${above['tablet.lg']} {
            width: 100%;
        }

        p {
            text-indent: 0;
        }
    }
`;

const ImageTextBlockComponent = ({
    content,
    media,
    layout = 'fullWidth',
    imageLoading = 'lazy',
}: ImageTextBlockComponentProps) => {
    if (!content) {
        return null;
    }

    const { enabledContent, heading, richText, buttons } = content;
    const { text: headingText = '', level: headingLevel = 'h2' } = heading ?? {};

    const showHeading = enabledContent?.includes('heading') && headingText;
    const showParagraph = enabledContent?.includes('paragraph') && Array.isArray(richText) && richText.length > 0;
    const showButtons = enabledContent?.includes('buttons') && Array.isArray(buttons) && buttons.length > 0;
    const hasTextContent = showHeading || showParagraph || showButtons;

    const isQuarterWidth = layout === 'quarterWidth';

    const headingTypography: Array<string | null> = isQuarterWidth
        ? ['Primary/Mobile/18_120_510_-3', null, null, null, 'Primary/15_120_510_-1']
        : ['Primary/Mobile/18_120_510_-3', null, null, null, 'Primary/20_120_510_-3'];

    const paragraphTypography: Array<string | null> = isQuarterWidth
        ? ['Primary/Mobile/18_120_510_-3', null, null, null, 'Primary/15_120_510_-1']
        : ['Secondary/Mobile/18_120_400_-1_Indent', null, null, null, 'Secondary/20_120_400_-1_Indent'];

    const buttonSize = isQuarterWidth ? ('small' as const) : ('large' as const);
    const mediaMode = layout === 'fullWidth' ? 'auto' : 'portrait';

    return (
        <Container>
            {media && (
                <ImageContainer data-layout={layout}>
                    <SanityBackgroundMediaSwitch
                        media={media}
                        breakpoint="tablet.lg"
                        mode={mediaMode}
                        sizes={getLayoutImageSizes(layout)}
                        loading={imageLoading}
                    />
                </ImageContainer>
            )}
            {hasTextContent && (
                <TextArea data-layout={layout}>
                    <TextContent data-layout={layout}>
                        {showHeading && (
                            <Text $as={headingLevel} typography={headingTypography}>
                                {headingText}
                            </Text>
                        )}
                        {showParagraph && <PortableText value={richText} typography={paragraphTypography} />}
                    </TextContent>
                    {showButtons && (
                        <div>
                            {buttons.map((button: ButtonLinkArrayComponent[number]) => {
                                const buttonType = button.type;
                                if (buttonType !== 'buttonLinkComponent') {
                                    return null;
                                }

                                return (
                                    <Button
                                        key={button.key}
                                        theme="textButtonUnderline"
                                        sanityLink={button}
                                        size={buttonSize}
                                    >
                                        {button.label}
                                    </Button>
                                );
                            })}
                        </div>
                    )}
                </TextArea>
            )}
        </Container>
    );
};

export default ImageTextBlockComponent;
